import DeleteIconButton from '../components/DeleteIconButton';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 as uuidv4 } from 'uuid';
import React from 'react';
import PropTypes from 'prop-types';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import CategoryCheckboxes from './CategoryCheckboxes';
import DraggableItem from './DraggableItem';
import ExpandableContainer from './ExpandableContainer';

const isDebugging = false;

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: ' #FCFCFE',
        [theme.breakpoints.down('sm')]: {
            paddingBottom: '8rem !important',
        },
    },
    styledBox: {
        // margin: '1rem',
        padding: '2rem',
        backgroundColor: 'white',
        height: '100%',
        borderRadius: '12px',
        margin: '0rem 1rem 1rem 1rem',
        [theme.breakpoints.down('sm')]: {
            padding: '1rem',
            margin: '0rem',
            overflowY: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
            border: '1px solid #D8D8D8',
        },
    },
}));

const AssociatedTagCategories = ({ trip, hasWritePerm, onChange, isParentTouched }) => {
    const classes = useStyles();

    useEffect(() => {
        const needsUpdate = trip.tag_categories.some(cat => !cat.idx);
        if (needsUpdate) {
            const newTagCategories = trip.tag_categories.map(cat => ({
                ...cat,
                idx: cat.idx ?? uuidv4()
            }));
            onChange((prevState) => ({ ...prevState, tag_categories: newTagCategories }));
        }
    }, [trip.tag_categories, onChange]);

    const checkinCategoriesCount = useMemo(() =>
        trip.tag_categories.filter(category => category.checkin).length,
        [trip.tag_categories]);

    const boardingCategoriesCount = useMemo(() =>
        trip.tag_categories.filter(category => category.boarding).length,
        [trip.tag_categories]);

    const onAssociatedTagCategoryAdd = useCallback(() => {
        onChange((prevState) => {
            const new_category = {
                idx: uuidv4(),
                sequence: Math.max(...prevState.tag_categories.map(cat => cat.sequence ?? 0), -1) + 1,
                category: null,
                chat_conversation: false,
                group_tour: prevState.id,
                guides_from_tags: false,
                location_sharing: false,
                location_all_if_no_tag: false,
                max_tags: 0,
                min_tags: 0,
                user_association: false,
                user_selected: false,
                checkin: false,
                boarding: false,
                copy_tags_to_subbookings: true,
                is_new: true,
            };

            return { ...prevState, tag_categories: [...prevState.tag_categories, new_category] };
        });
    }, [onChange]);

    const onAssociatedTagCategoryChange = useCallback((ev, selected_category, targetIdx) => {
        onChange((prevState) => {
            if (selected_category?.id) {
                const selectedId = selected_category.id.toString();
                const duplicate = prevState.tag_categories.some((c) => 
                    c.idx !== targetIdx && 
                    c.category?.id?.toString() === selectedId
                );
                if (duplicate) return prevState;
            }
            const tag_categories = prevState.tag_categories.map((atc) => atc.idx === targetIdx ? { ...atc, category: selected_category } : atc);
            return { ...prevState, tag_categories };
        });
    }, [onChange]);

    const onAssociatedCategoryCheckboxChange = useCallback((ev, idx, field) => {
        ev.stopPropagation();
        onChange((prevState) => {
            let update = prevState.tag_categories.filter((atc) => atc.idx === idx)[0];
            let removedOld = prevState.tag_categories.filter((atc) => atc.idx !== idx);
            if (field === 'required') {
                update = { ...update, min_tags: ev.target.checked ? 1 : 0 };
            }
            if (field === 'guides_from_tags') {
                update = { ...update, guides_from_tags: ev.target.checked };
            }
            if (field === 'guides_from_tags' && ev.target.checked) {
                update = { ...update, user_association: false, guides_from_tags: ev.target.checked };
            }
            if (field === 'user_association' && ev.target.checked) {
                update = { ...update, user_association: ev.target.checked, guides_from_tags: false };
            }
            if (field === 'chat_conversation') {
                update = { ...update, chat_conversation: ev.target.checked };
            }
            if (field === 'visible_in_chat') {
                update = { ...update, visible_in_chat: ev.target.checked };
            }
            if (field === 'user_association') {
                update = { ...update, user_association: ev.target.checked };
            }
            if (field === 'location_sharing') {
                if (ev.target.checked === false) {
                    update = { ...update, location_sharing: ev.target.checked, user_association: false };
                } else {
                    update = { ...update, location_sharing: ev.target.checked };
                }
            }
            if (field === 'user_selected') {
                update = { ...update, user_selected: ev.target.checked };
            }
            if (field === 'location_all_if_no_tag') {
                update = { ...update, location_all_if_no_tag: ev.target.checked };
            }
            if (field === 'max_tags') {
                update = { ...update, max_tags: ev.target.checked ? 1 : 0 };
            }
            if (field === 'allow_multiple_tags') {
                // 32767 is a fair random number. Anything > 1 will work. Jira #CP-4920.
                // The purpose here is not to impose a limit on the number of tags, but to tell the backend
                // to use an object with all tags from the category when a rendering context is created.
                update = { ...update, max_tags: ev.target.checked ? /* max int value = */ 32767 : 0 }
            }
            if (field === 'remove_upon_date_changes') {
                update = { ...update, remove_upon_date_changes: ev.target.checked };
            }
            if (field === 'cleanup_on_assignment') {
                update = { ...update, cleanup_on_assignment: ev.target.checked };
            }
            if (field === 'departure_wide') {
                update = { ...update, departure_wide: ev.target.checked };
            }
            if (field === 'remove_guides_on_cleanup') {
                update = { ...update, remove_guides_on_cleanup: ev.target.checked };
            }
            if (field === 'remove_unreferenced_guides') {
                update = { ...update, remove_unreferenced_guides: ev.target.checked };
            }
            if (field === 'copy_tags_to_guides') {
                update = { ...update, copy_tags_to_guides: ev.target.checked };
            }
            if (field === 'copy_tags_to_subbookings') {
                update = { ...update, copy_tags_to_subbookings: ev.target.checked };
            }
            return { ...prevState, tag_categories: [...removedOld, update] };
        });
    }, [onChange]);

    const onCheckinBoardingCheckboxChange = (ev, idx, id, field) => {
        ev.stopPropagation();

        onChange((prevState) => {
            const target = prevState.tag_categories.find((atc) => (id && atc.id === id) || (idx && atc.idx === idx));
            if (!target) return prevState;

            const updatedTagCategories = prevState.tag_categories.map((atc) => {
                if ((id && atc.id === id) || (idx && atc.idx === idx)) {
                    const updatedCategory = { ...atc, [field]: ev.target.checked };
                    if (field === 'boarding') {
                        updatedCategory.min_tags = ev.target.checked;
                    }
                    return updatedCategory;
                }
                return atc;
            });

            return { ...prevState, tag_categories: updatedTagCategories };
        });
    };

    const onAssociatedCategoryDelete = useCallback((targetIdx) => {
        onChange((prevState) => {
            const items = prevState.tag_categories.filter((atc) => atc.idx !== targetIdx);
            return { ...prevState, tag_categories: items };
        });
    }, [onChange]);

    const moveCategory = useCallback((dragIdx, hoverIdx) => {
        if (dragIdx === hoverIdx) return;

        onChange((prevState) => {
            const items = [...prevState.tag_categories];
            const dragIndex = items.findIndex(cat => cat.idx === dragIdx);
            const hoverIndex = items.findIndex(cat => cat.idx === hoverIdx);

            if (dragIndex === -1 || hoverIndex === -1) return prevState;

            if (dragIndex === hoverIndex) return prevState;

            const draggedItem = items[dragIndex];

            items.splice(dragIndex, 1);
            items.splice(hoverIndex, 0, draggedItem);

            const updatedItems = items.map((item, index) => ({
                ...item,
                sequence: index
            }));

            return { ...prevState, tag_categories: updatedItems };
        });
    }, [onChange]);



    return (
        <div className={classes.root}>
            <Grid container direction="row" spacing={2} sx={{ marginBottom: '1rem' }}>
                <Grid item xs={12} lg={8}>
                    <Grid container direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item>
                            <Typography fontWeight={500}>Associated Tag Categories</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12} lg={8}>
                    <DndProvider backend={HTML5Backend}>
                        {trip.tag_categories?.length > 0 ? (
                            trip.tag_categories
                                .filter(cat => {
                                    // Hide empty rows created for check-in/boarding sections
                                    if ((cat.checkin || cat.boarding) && !cat.category?.id) {
                                        return false;
                                    }
                                    return true;
                                })
                                .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
                                .map((cat, index) => (
                                    <DraggableItem
                                        key={cat.idx || cat.id || cat.category?.id || index}
                                        idx={cat.idx}
                                        moveItem={moveCategory}
                                        hasWritePerm={hasWritePerm}
                                        type="category"
                                    >
                                        <Grid item container spacing={2} xs={12} alignItems={'center'}>
                                            <ExpandableContainer
                                                header={
                                                    <CategoryHeader
                                                        cat={cat}
                                                        hasWritePerm={hasWritePerm}
                                                        onAssociatedTagCategoryChange={onAssociatedTagCategoryChange}
                                                        onAssociatedCategoryDelete={onAssociatedCategoryDelete}
                                                        index={index}
                                                        isParentTouched={isParentTouched}
                                                        tagCategories={trip.tag_categories}
                                                        trip={trip}
                                                    />
                                                }
                                                body={
                                                    <CategoryBody
                                                        cat={cat}
                                                        checkinCategoriesCount={checkinCategoriesCount}
                                                        boardingCategoriesCount={boardingCategoriesCount}
                                                        onAssociatedCategoryCheckboxChange={onAssociatedCategoryCheckboxChange}
                                                        onCheckinBoardingCheckboxChange={onCheckinBoardingCheckboxChange}
                                                    />
                                                }
                                                defaultOpen={cat.is_new}
                                            />
                                        </Grid>
                                    </DraggableItem>
                                ))
                        ) : (
                            <Typography style={{ fontSize: '90%', marginBottom: '1rem', paddingLeft: '1rem' }}>
                                No tag categories found for this trip.
                            </Typography>
                        )}
                    </DndProvider>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Button
                    onClick={onAssociatedTagCategoryAdd}
                    color="primary"
                    variant="outlined"
                >
                    Add Category
                </Button>
            </Grid>
        </div>
    );
};

const CategoryHeader = memo(({ cat, hasWritePerm, onAssociatedTagCategoryChange, onAssociatedCategoryDelete, index, isParentTouched, tagCategories, trip }) => {
    const [isTouched, setIsTouched] = useState(false);

    const handleChange = (ev, selectedCategory) => {
        setIsTouched(true);
        onAssociatedTagCategoryChange(ev, selectedCategory, cat.idx);
    };

    // Remove current category ID if it exists (for editing existing categories)
    const currentCategoryId = cat.category?.id?.toString();

    // Build API filters array: exclude categories already used in any section
    const apiFilters = [];
    const allUsedIds = [...new Set(tagCategories
        .filter(c => c.category?.id)
        .map(c => c.category.id.toString()))];
    const excludeIds = currentCategoryId ? allUsedIds.filter(id => id !== currentCategoryId) : allUsedIds;
    if (excludeIds.length > 0) {
        apiFilters.push({ field: 'id', op: 'not_in', value: excludeIds });
    }
    if (trip.is_bespoke) {
        apiFilters.push({ field: 'code', op: 'ne', value: 'departure' });
    }

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs>
                <RemoteAutocomplete
                    resource="tour_tag_category"
                    label="Tag Category"
                    variant="filled"
                    size="small"
                    disabled={!cat.is_new || !hasWritePerm}
                    disableClearable={true}
                    onChange={handleChange}
                    value={cat.category}
                    getOptionLabel={(opt) => opt.name}
                    apiFilters={apiFilters}
                    textFieldProps={{
                        onBlur: () => setIsTouched(true),
                        error: (isParentTouched || isTouched) && !cat.category?.id,
                        helperText: (isParentTouched || isTouched) && !cat.category?.id ? 'Please select a category' : undefined
                    }}
                />
            </Grid>
            {(trip?.id === 'operator-defaults' || cat.is_new) && (
                <Grid item>
                    <DeleteIconButton allowed={hasWritePerm} size="medium" fontSize="medium" onClick={() => onAssociatedCategoryDelete(cat.idx)} />
                </Grid>
            )}
        </Grid>
    );
});

const CategoryBody = memo(({
    cat,
    checkinCategoriesCount,
    boardingCategoriesCount,
    onAssociatedCategoryCheckboxChange,
    onCheckinBoardingCheckboxChange
}) => (
    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '0.75rem', marginTop: '0.25rem' }}>
        {isDebugging && <Typography>idx {cat.idx} - id {cat.category?.id} - sequence {cat.sequence}</Typography>}
        <CategoryCheckboxes
            cat={cat}
            checkinCategoriesCount={checkinCategoriesCount}
            boardingCategoriesCount={boardingCategoriesCount}
            onAssociatedCategoryCheckboxChange={onAssociatedCategoryCheckboxChange}
            onCheckinBoardingCheckboxChange={onCheckinBoardingCheckboxChange}
        />
    </div>
));

CategoryHeader.displayName = 'CategoryHeader';
CategoryBody.displayName = 'CategoryBody';

AssociatedTagCategories.propTypes = {
    trip: PropTypes.shape({
        tag_categories: PropTypes.array.isRequired,
        is_bespoke: PropTypes.bool,
    }).isRequired,
    hasWritePerm: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    isParentTouched: PropTypes.bool,
};

CategoryHeader.propTypes = {
    cat: PropTypes.shape({
        idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), name: PropTypes.string }),
        is_new: PropTypes.bool,
        sequence: PropTypes.number,
    }).isRequired,
    hasWritePerm: PropTypes.bool,
    onAssociatedTagCategoryChange: PropTypes.func.isRequired,
    onAssociatedCategoryDelete: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    isParentTouched: PropTypes.bool,
    tagCategories: PropTypes.array.isRequired,
    trip: PropTypes.shape({ is_bespoke: PropTypes.bool }).isRequired,
};

CategoryBody.propTypes = {
    cat: PropTypes.shape({
        idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category: PropTypes.object,
        sequence: PropTypes.number,
    }).isRequired,
    checkinCategoriesCount: PropTypes.number.isRequired,
    boardingCategoriesCount: PropTypes.number.isRequired,
    onAssociatedCategoryCheckboxChange: PropTypes.func.isRequired,
    onCheckinBoardingCheckboxChange: PropTypes.func.isRequired,
};

export default AssociatedTagCategories;
